import React from 'react';
import { Link } from 'react-router-dom';
import { Star, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';

const fmt = (n) => '\u20B9 ' + n.toLocaleString('en-IN');

const ProductCard = ({ product }) => {
  const { addToCart } = useCart();
  const { toast } = useToast();
  const productHref = product.variantId
    ? `/product/${product.productId || product.id}?variant=${encodeURIComponent(product.variantId)}`
    : `/product/${product.productId || product.id}`;
  const hasStock = product.stock !== undefined && product.stock !== null;
  const stock = Number(product.stock ?? 0);
  const showLowStock = hasStock && stock < 10;

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
    toast({ title: 'Added to cart', description: product.name });
  };

  return (
    <Link
      to={productHref}
      className="group block bg-white border border-[#1F2A44]/8 hover:border-[#E8B530] hover:shadow-xl transition-all duration-300 overflow-hidden"
    >
      <div className="relative aspect-square overflow-hidden bg-[#F3EFE8]">
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        {product.discount > 0 && (
          <span className="absolute top-3 left-3 bg-[#E8B530] text-[#1F2A44] text-[11px] font-bold px-2 py-1 rounded-sm">
            {product.discount}% OFF
          </span>
        )}
        <button
          onClick={handleAdd}
          aria-label="Add to cart"
          className="absolute bottom-3 right-3 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#1F2A44] text-[#FAF7F2] flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 translate-y-0 sm:translate-y-2 sm:group-hover:translate-y-0 transition-all duration-300 hover:bg-[#E8B530] hover:text-[#1F2A44] shadow-md"
        >
          <ShoppingBag size={16} />
        </button>
      </div>

      <div className="p-4">
        {showLowStock && (
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-red-700">
            {stock > 0 ? `Hurry, only ${stock} left` : 'Out of stock'}
          </p>
        )}
        <h3 className="text-[#1F2A44] text-sm font-medium leading-snug line-clamp-2 min-h-[40px]">
          {product.name}
        </h3>
        {product.variantLabel && (
          <p className="mt-1 text-xs text-[#1F2A44]/60 line-clamp-1">{product.variantLabel}</p>
        )}
        <div className="flex items-center gap-1 mt-2 text-xs text-[#1F2A44]/70">
          <Star size={12} className="fill-[#E8B530] text-[#E8B530]" />
          <span className="font-semibold">{product.rating ? Number(product.rating).toFixed(1) : 4.5}</span>
          <span>({product.reviews || 0} reviews)</span>
        </div>
        <div className="mt-3 flex items-baseline gap-2 flex-wrap">
          <span className="text-[#1F2A44] font-bold text-base">{fmt(product.price)}</span>
          {product.marketPrice > product.price && (
            <span className="text-[#1F2A44]/50 line-through text-xs">{fmt(product.marketPrice)}</span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
